<template>
    <div class="w-full flex flex-wrap">
        <div class="mt-2 flex flex-col w-full">
            <div class="">
                <div class="inline-block min-w-full align-middle" :id="printPreviewId">
                    <page-headline>{{$t('pages.analytics.index.title')}}</page-headline>
                    <period-filter
                        :auction-years="auctionYears"
                        :selected-aggregation="getAggregation"
                        :selected-year="getYear"
                        :selected-start-date="getStartDate"
                        :selected-end-date="getEndDate"
                        ref="filter"
                        @change="filterChanged"
                    />
                    <div class="mt-8 overflow-hidden">
                        <div>
                            <h3 class="text-xl leading-6 font-medium" v-if="getAggregation < 2">{{$t('shared.pyramid_auction.for_year', {year: this.$refs.filter?.year})}}</h3>
                            <h3 class="text-xl leading-6 font-medium" v-if="getAggregation === 2 && !isDefaultPeriod()">{{$t('shared.pyramid_auction.for_period', {start: this.formatStartDate(), end: this.formatEndDate()})}}</h3>
                            <h3 class="text-xl leading-6 font-medium" v-if="getAggregation === 2 && isDefaultPeriod()">{{$t('shared.pyramid_auction.for_default_period', {days: this.defaultPeriod})}}</h3>
                            <dl class="mt-5 grid grid-cols-1 bg-white overflow-hidden tile divide-y divide-gray-200 md:grid-cols-3 md:divide-y-0 md:divide-x">
                                <comparison-badge
                                    :title="$t('shared.pyramid_auction.visitors')"
                                    :value="parseInt(comparisonDataBase?.visitors)"
                                    :comparison-value="parseInt(comparisonData?.visitors)"
                                    :format-as="`straightNumber`"
                                    :format-comparison-value-as="`straightNumber`"
                                />
                                <comparison-badge
                                    :title="$t('shared.pyramid_auction.floor_sales' + this.$page.props.tenant.labels_key)"
                                    :value="parseFloat(comparisonDataBase?.floor_sales)"
                                    :comparison-value="parseFloat(comparisonData?.floor_sales || 0)"
                                    :format-as="`currency`"
                                    :format-comparison-value-as="`currency`"
                                />
                                <comparison-badge
                                    :title="$t('shared.pyramid_auction.bids_per_visitors')"
                                    :value="parseFloat(comparisonDataBase?.bids_per_visitor)"
                                    :unit="`%`"
                                    :comparison-value="parseFloat(comparisonData?.bids_per_visitor)"
                                    :comparison-unit="`%`"
                                    :format-as="`straightNumber`"
                                    :format-comparison-value-as="`straightNumber`"
                                />
                            </dl>
                        </div>
                    </div>

                    <div
                        class="mt-8 w-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
                        v-if="sortedAuctions && sortedAuctions.length > 0"
                    >
                        <table class="table-default">
                            <thead>
                                <tr>
                                    <th scope="col">
                                        <sortable-column
                                            :text="$t('shared.pyramid_auction.table_columns.date')"
                                            :active="isActive('date_start')"
                                            :sort-direction="sortDirection"
                                            @sortColumn="sortByColumn('date_start')"
                                        />
                                    </th>
                                    <th scope="col">
                                        <sortable-column
                                            :text="$t('shared.pyramid_auction.table_columns.title')"
                                            :active="isActive('name')"
                                            :sort-direction="sortDirection"
                                            @sortColumn="sortByColumn('name')"
                                        />
                                    </th>
                                    <th scope="col">
                                        <sortable-column
                                            :text="$t('shared.pyramid_auction.table_columns.quantity_sold')"
                                            :active="isActive('sold_quantity')"
                                            :sort-direction="sortDirection"
                                            @sortColumn="sortByColumn('sold_quantity')"
                                        />
                                    </th>
                                    <th scope="col">
                                        <sortable-column
                                            :text="$t('shared.pyramid_auction.table_columns.average_price')"
                                            :active="isActive('average_price')"
                                            :sort-direction="sortDirection"
                                            @sortColumn="sortByColumn('average_price')"
                                        />
                                    </th>
                                    <th scope="col">
                                        <sortable-column
                                            :text="$t('shared.pyramid_auction.table_columns.floor_sales' + this.$page.props.tenant.labels_key)"
                                            :active="isActive('floor_sales')"
                                            :sort-direction="sortDirection"
                                            @sortColumn="sortByColumn('floor_sales')"
                                        />
                                    </th>
                                    <th scope="col">
                                        <sortable-column
                                            :text="$t('shared.pyramid_auction.table_columns.merch_sales' + this.$page.props.tenant.labels_key)"
                                            :active="isActive('merch_sales')"
                                            :sort-direction="sortDirection"
                                            @sortColumn="sortByColumn('merch_sales')"
                                        />
                                    </th>
                                    <th scope="col">
                                        <sortable-column
                                            :text="$t('shared.pyramid_auction.table_columns.number_visitors')"
                                            :active="isActive('visitor_count')"
                                            :sort-direction="sortDirection"
                                            @sortColumn="sortByColumn('visitor_count')"
                                        />
                                    </th>
                                    <th scope="col">
                                        <sortable-column
                                            :text="$t('shared.pyramid_auction.table_columns.bids_per_visitors')"
                                            :active="isActive('bids_per_visitor')"
                                            :sort-direction="sortDirection"
                                            @sortColumn="sortByColumn('bids_per_visitor')"
                                        />
                                    </th>
                                    <th scope="col">
                                        <sortable-column
                                            :text="$t('shared.pyramid_auction.table_columns.number_of_levels')"
                                            :active="isActive('number_of_levels')"
                                            :sort-direction="sortDirection"
                                            @sortColumn="sortByColumn('number_of_levels')"
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-if="sortedAuctions"
                                    v-for="(auction, index) in sortedAuctions" :key="auction.id"
                                    @click="goToDetails(auction.id ? auction.id : index)"
                                    class="cursor-pointer hover:bg-gray-100"
                                    :class="{'bg-gray-100' : activeAuctionId === auction.id}"
                                >
                                    <td>{{auction.date_start | formatDay }}</td>
                                    <td>
                                        <shorten :value="auction.name" :tooltip-class="'tooltip-auction-name'"/>
                                    </td>
                                    <td>{{ auction.sold_quantity }}</td>
                                    <td>{{ auction.average_price | toCurrency }}</td>
                                    <td>{{ auction.floor_sales | toCurrency }}</td>
                                    <td>{{ auction.merch_sales | toCurrency }}</td>
                                    <td>{{ auction.visitors }}</td>
                                    <td>{{ auction.bids_per_visitor }}{{ auction.bids_per_visitor > 0.0 ? ' %' : ''}}</td>
                                    <td>{{ auction.number_of_passed_levels }}</td>
                                </tr>
                                <tr class="summary">
                                    <td>{{$t('shared.pyramid_auction.table_summary.total_average')}}</td>
                                    <td></td>
                                    <td>{{ totalSoldQuantity }}</td>
                                    <td>{{ totalAveragePrice | toCurrency }}</td>
                                    <td>{{ totalFloorSales | toCurrency }}</td>
                                    <td>{{ totalMerchSales | toCurrency }}</td>
                                    <td>{{ totalNumberOfVisitors }}</td>
                                    <td>{{ totalBidsVsVisitors }}% {{$t('shared.pyramid_auction.table_summary.av')}}</td>
                                    <td>{{ averageNumberOfLevels }} {{$t('shared.pyramid_auction.table_summary.av')}}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div class="mb-6 px-6" v-if="auctions && auctions.total > auctions.per_page">
                            <pagination :collection="auctions" :url="$page.props.paginate.currentUrl" :params="this.form" />
                        </div>
                    </div>
                    <div class="bg-white tile my-5" v-if="!sortedAuctions || !sortedAuctions.length || sortedAuctions.length < 1">
                        <div class="px-4 py-5 sm:p-6">
                            <div class="flex sm:items-start sm:justify-between">
                                <div class="flex">
                                    <h3 class="text-xl leading-6 font-medium mr-5"><icon name="emoji-sad" class="h-10"/></h3>
                                    <div class="mt-2 max-w-xl text-sm text-gray-500">
                                        <p>{{$t('pages.controlpanel.index.no_past_auction_found')}}</p>
                                    </div>
                                </div>
                                <div class="mt-5 sm:mt-0 sm:ml-6 sm:flex-shrink-0 sm:flex sm:items-center">
                                    <button @click="goToPlanning()" type="button" class="btn btn-primary">
                                        {{$t('pages.controlpanel.index.go_to_planning_button_label')}}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import { Link } from '@inertiajs/vue2';
import Icon from '@/Shared/Icon';
import PageHeadline from "../PageHeadline";
import SortableColumn from "@/Shared/SortableColumn";
import {SortDirections} from "@/mixins/SortDirections";
import ComparisonBadge from "./ComparisonBadge";
import PeriodFilter from "./PerriodFilter";
import moment from "moment";
import Pagination from '@/Shared/Pagination';
import Shorten from "../Shorten.vue";


export default {
    mixins: [
        SortDirections
    ],
    components: {
        PageHeadline,
        SortableColumn,
        Link,
        Icon,
        ComparisonBadge,
        PeriodFilter,
        Pagination,
        Shorten
    },
    props: {
        auctions: Object,
        activeAuction: Object,
        comparison: {
            type: Array,
        },
        auctionYears: Array,
        initialSortColumn: String,
        initialSortDirection: String,
        defaultPeriod: {
            type: Number,
            default: 30
        }
    },
    data() {
        let defaultEndDate = moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD');
        let defaultStartDate = moment(new Date()).subtract(this.defaultPeriod + 1, 'days').format('YYYY-MM-DD');
        return {
            sortColumn: this.initialSortColumn,
            sortDirection: this.initialSortDirection,
            sortedAuctions: {},
            printPreviewId: '',
            defaultStartDate: defaultStartDate,
            defaultEndDate: defaultEndDate,
            form: {}
        }
    },
    mounted() {
        if (this.sortDirection.length < 1) {
            this.sortDirection = SortDirections.data().DESCENDING;
        }
        // if auctions present, sort them
        if (this.auctions && this.auctionData.length > 0 ) {
            this.sortedAuctions = this.sortAuctions(this.auctionData, this.sortColumn, this.sortDirection);
            this.$emit('sorting-changed', this.sortColumn, this.sortDirection);
        }
    },
    created() {
        this.generateId();
        this.updateFilterValues();
    },
    computed: {
        comparisonDataBase() {
            this.comparison[0].bids_per_visitor = this.calculateBidsPerVisits(0);
            return this.comparison[0];
        },
        comparisonData() {
            this.comparison[1].bids_per_visitor = this.calculateBidsPerVisits(1);
            return this.comparison[1];
        },
        activeAuctionId() {
            if (this.activeAuction && this.activeAuction.id) {
                return this.activeAuction.id;
            }
            return null;
        },
        totalSoldQuantity() {
            return this.sortedAuctions.reduce((acc, auction) => {
                return acc + parseInt(auction.sold_quantity);
            }, 0);
        },
        totalAveragePrice() {
            let totalQuantity = 0;
            let totalAmount = 0;
            this.auctionData.map((auction) => {
                totalQuantity += parseInt(auction.sold_quantity);
                totalAmount += parseFloat(auction.floor_sales);
            });
            if (totalQuantity === 0 ) {
                return 0;
            }
            return totalAmount / totalQuantity;
        },
        totalFloorSales()  {
            let total = 0;
            this.auctionData.map((auction) => {
                total += auction?.floor_sales;
            });
            return total;
        },
        totalMerchSales()  {
            let total = 0;
            this.auctionData.map((auction) => {
                total += auction?.merch_sales;
            });
            return total;
        },
        totalNumberOfVisitors() {
            if ( this.auctionData.length === 0 ){
                return 0;
            }
            return this.auctionData.reduce((acc, auction) => {
                return acc + (auction?.visitors ? auction?.visitors : 0);
            }, 0);
        },
        totalBidsVsVisitors() {
            let totalBids = 0;
            let totalVisits = 0;
            this.auctionData.map((auction) => {
                totalBids += auction.bids?.length ?? 0;
                totalVisits += auction.visitors ?? 0;
            });
            if (this.auctionData.length === 0 ) {
                return 0;
            }
            return totalVisits > 0 ? (totalBids / totalVisits * 100).toFixed(0) : 0;
        },
        averageNumberOfLevels() {
            if (this.auctionData.length === 0 ) {
                return 0;
            }
            return (this.auctionData.reduce((acc, auction) => {
                return acc + auction.number_of_passed_levels;
            }, 0) / this.auctionData.length).toFixed(1);
        },
        getAggregation() {
            return parseInt(this.$route().params?.aggregation ?? this.$page.props.tenant.analytics.filter_aggregation);
        },
        getYear() {
            return parseInt(this.$route().params?.year) ?? new Date().getFullYear();
        },
        getStartDate() {
            if (this.$route().params?.dateStart) {
                let dateParts = this.$route().params?.dateStart.split('-');
                return moment(new Date(dateParts[0], dateParts[1]-1, dateParts[2])).format('YYYY-MM-DD');
            }
            return this.defaultStartDate;
        },
        getEndDate() {
            if (this.$route().params?.dateEnd) {
                let dateParts = this.$route().params?.dateEnd.split('-');
                return moment(new Date(dateParts[0], dateParts[1]-1, dateParts[2])).format('YYYY-MM-DD');
            }
            return this.defaultEndDate;
        },
        auctionData() {
            return (this.auctions && this.auctions.data) ? Object.values(this.auctions.data) : [];
        }
    },
    methods: {
        soldQuantity(auction) {
            return auction.levels.reduce((accumulator, level) => {
                return accumulator + level.quantity_used;
            }, 0);
        },
        numberOfVisitors(auction) {
            return auction?.visitors ? auction?.visitors : 0;
        },
        bidsVsVisitors(auction) {
            let visitors = this.numberOfVisitors(auction);
            if (visitors === 0 ) {
                return 0;
            }
            return auction.bids?.length && auction.bids?.length > 0 ? Math.round(auction.bids?.length / visitors * 100) : 0;
        },
        numberOfLevels(auction) {
            return auction.levels?.length;
        },
        goToDetails(auctionId) {
            if (this.$refs.filter.aggregation === 2) {
                this.$emit('go-to-details', auctionId, this.getFilterValues());
            } else {
                let auction = this.sortedAuctions.find((auction) => auction.id == auctionId);
                let data = this.getFilterValues();
                data.aggregation = 2; // Change to the days list
                if (auction) {
                    data.dateStart = auction.date_start?.split(' ')[0];
                    data.dateEnd = auction.last_date?.split(' ')[0];
                }
                this.$refs.filter.setFilterValues(data);
            }
        },
        sortByColumn(columnName) {
            // Sort the data here not on the server
            if (this.isActive(columnName)) {
                this.sortDirection = this.sortDirection === SortDirections.data().ASCENDING ? SortDirections.data().DESCENDING : SortDirections.data().ASCENDING;
            }
            this.sortColumn = columnName;
            this.sortedAuctions = this.sortAuctions(this.auctionData, this.sortColumn, this.sortDirection);
            this.$emit('sorting-changed', this.sortColumn, this.sortDirection);
        },
        isActive(columnName) {
            return this.sortColumn === columnName;
        },
        sortAuctions(auctions, sortColumn, sortDirection) {
            return auctions.sort((a,b) => {
                let valA = a[sortColumn];
                let valB = b[sortColumn];
                if (sortDirection === SortDirections.data().ASCENDING) {
                    return valA < valB ? -1 : valA > valB ? 1 : 0;
                }
                return valA > valB ? -1 : valA < valB ? 1 : 0;
            });
        },
        goToPlanning() {
            this.$inertia.visit(route('auctions.planning'));
        },
        generateId() {
            if (this.printPreviewId.length === 0 ){
                this.printPreviewId = 'auction-performance-details-' + Math.floor(Math.random() * 100000000000).toString();
            }
        },
        filterChanged() {
            let filterValues = this.$refs.filter?.getFilterValues();
            filterValues.sortBy = this.sortColumn;
            filterValues.order = this.sortDirection;
            if (filterValues && true === filterValues.valid) {
                let route = this.route('auctions.analytics', filterValues);
                this.$inertia.get(route, {}, { only: ['auctions', 'comparison', 'auction', 'sortBy', 'order'] });
            }
        },
        formatStartDate(){
            return this.$options.filters.formatDay(this.$refs.filter?.dateStart);
        },
        formatEndDate() {
            return this.$options.filters.formatDay(this.$refs.filter?.dateEnd);
        },
        calculateAuctionsValues() {
            this.updateFilterValues();
        },
        updateFilterValues(){
            this.form = this.getFilterValues();
        },
        getFilterValues() {
            if (this.$refs.filter) {
                let filters = this.$refs.filter?.getFilterValues();
                filters.sortColumn = this.sortColumn;
                filters.sortDirection = this.sortDirection;
                return filters;
            } else {
                let aggregation = this.getAggregation;
                switch (aggregation) {
                    case 0:
                    case 1:
                        return {
                            aggregation: aggregation,
                            year: this.getYear,
                            sortColumn: this.sortColumn,
                            sortDirection: this.sortDirection
                        }
                    default:
                        return {
                            aggregation: aggregation,
                            year: this.getYear,
                            dateStart: this.getStartDate,
                            dateEnd: this.getEndDate,
                            sortColumn: this.sortColumn,
                            sortDirection: this.sortDirection
                        }
                }
            }
        },
        calculateBidsPerVisits(index) {
            if (!this.comparison[index].hasOwnProperty('bids_per_visitor')) {
                if (this.comparison[index].visitors > 0) {
                    return this.comparison[index].bids / this.comparison[index].visitors * 100;
                }
            }
            return 0;
        },
        isDefaultPeriod() {
            if (this.getAggregation === 2) {
                return this.getStartDate === this.defaultStartDate && this.getEndDate === this.defaultEndDate;
            }
        },
    },
    watch: {
        auctions: {
            handler: 'calculateAuctionsValues',
            immediate: true
        }
    }
}
</script>
